"use client"

import {
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  safePolygon,
  useClick,
  useDismiss,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
  useTransitionStyles,
  useTypeahead,
} from "@floating-ui/react"
import { Check, ChevronRight } from "lucide-react"
import { useReducedMotion } from "motion/react"
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
} from "react"
import { DURATION_BASE, DURATION_QUICK, EASE_OUT_CSS } from "../../lib/ease"
import { cn } from "../../lib/utils"
import { useFloatingOverlay } from "./use-floating-overlay"

/**
 * Trigger-anchored menu with nested submenus.
 *
 * v2 already had `Popover` (a panel with no menu semantics) and `ContextMenu`
 * (right-click, no nesting), but nothing that could express a dropdown whose
 * items open further menus — which is what a filter builder needs. This fills
 * that gap on `@floating-ui/react`, which supplies the parts that are painful
 * to hand-roll: collision-aware placement, a tree of nested floating nodes that
 * dismiss together, and `safePolygon` so the pointer can travel diagonally into
 * a submenu without it closing underneath.
 */

/**
 * The one row treatment shared by every v2 menu surface.
 *
 * Menu and ContextMenu used to express the same row twice and land 6.5px apart
 * — 29px against 35.5px, with different radius, padding, gap and type size.
 * This is the single definition both now compose from: a 32px rung off the v2
 * control ladder, an 8px inner radius (the 12px panel radius less its 4px
 * padding, so the corners stay concentric), and `text-v2-sm`.
 *
 * `min-h-8` rather than `h-8` so a wrapping or two-line item grows instead of
 * clipping.
 */
export const V2_MENU_ROW_CLASS =
  "flex min-h-8 w-full cursor-default select-none items-center gap-2 rounded-v2-lg px-2 py-1 text-left text-v2-sm outline-none"

/**
 * The one overlay surface shared by every v2 menu-family panel — `Menu`,
 * `MenuSub`, `Popover`, `ContextMenu` and `Select` — so a single edge and
 * shadow read consistently across all of them instead of each drifting its
 * own border/ring/shadow combination.
 */
export const V2_MENU_SURFACE_CLASS =
  /**
   * `z-[110]` sits above `Modal`'s overlay (`z-[100]`, see `modal.tsx:285`) so
   * a `Select`/`Popover`/`Menu` opened from inside a modal renders on top of
   * it. Every menu-family surface reuses this constant, so this is the single
   * place the layer floor is set.
   */
  "z-[110] overflow-y-auto overscroll-contain rounded-v2-xl border border-v2-border bg-v2-popover p-1 text-v2-popover-foreground shadow-v2-overlay outline-none"

/** The hover/focus treatment shared by every row across the v2 menu family. */
export const V2_MENU_ITEM_HOVER_CLASS =
  "transition-colors hover:bg-v2-accent focus-visible:bg-v2-accent"

interface MenuContextValue {
  getItemProps: (props?: Record<string, unknown>) => Record<string, unknown>
  activeIndex: number | null
  setActiveIndex: Dispatch<SetStateAction<number | null>>
  setOpen: (open: boolean) => void
  isOpen: boolean
}

const MenuContext = createContext<MenuContextValue | null>(null)

function useMenuContext() {
  const context = useContext(MenuContext)
  if (!context) throw new Error("Menu parts must be used inside a <Menu>")
  return context
}

export interface MenuProps {
  /** The element that opens the menu. A submenu renders its own trigger row. */
  trigger?: ReactNode
  /** Submenu label. Provide this instead of `trigger` for a nested menu. */
  label?: ReactNode
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  align?: "start" | "end"
  /**
   * Which side of the trigger a root menu opens on. Submenus always open to the
   * side regardless, so this only applies at the top level.
   */
  side?: "top" | "bottom" | "left" | "right"
  contentClassName?: string
  "aria-label"?: string
  disabled?: boolean
}

function MenuRoot({
  trigger,
  label,
  children,
  open: openProp,
  onOpenChange,
  align = "start",
  side = "bottom",
  contentClassName,
  "aria-label": ariaLabel,
  disabled,
}: MenuProps) {
  const reduce = useReducedMotion()
  const [internalOpen, setInternalOpen] = useState(false)
  const controlled = openProp !== undefined
  const isOpen = controlled ? openProp : internalOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [controlled, onOpenChange],
  )

  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const elementsRef = useRef<Array<HTMLButtonElement | null>>([])
  const labelsRef = useRef<Array<string | null>>([])

  const tree = useFloatingTree()
  const nodeId = useFloatingNodeId()
  const parentId = useFloatingParentNodeId()
  const isNested = parentId !== null

  // Read before this component provides its own: a submenu's trigger is an item
  // in its PARENT's list, so it has to take its props and active index from
  // there. Wiring it to this menu's own context instead leaves the parent's
  // navigation and the child's ArrowRight handler uncomposed, and the submenu
  // never opens from the keyboard.
  const parent = useContext(MenuContext)
  const item = useListItem()

  const { refs, overlayStyles, context } = useFloatingOverlay({
    nodeId,
    open: isOpen,
    onOpenChange: setOpen,
    placement: isNested
      ? "right-start"
      : (`${side}-${align}` as
          | "top-start"
          | "top-end"
          | "bottom-start"
          | "bottom-end"
          | "left-start"
          | "left-end"
          | "right-start"
          | "right-end"),
    gap: isNested ? { mainAxis: 4, alignmentAxis: -4 } : { mainAxis: 6 },
  })

  // A nested menu opens on hover with a safe polygon, so a diagonal pointer path
  // toward the submenu does not cross out of the parent item and close it.
  const hover = useHover(context, {
    enabled: isNested && !disabled,
    delay: { open: 75 },
    handleClose: safePolygon({ blockPointerEvents: true }),
  })
  const click = useClick(context, { enabled: !isNested && !disabled, toggle: !isNested })
  const role = useRole(context, { role: "menu" })
  const dismiss = useDismiss(context, { bubbles: true })
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    nested: isNested,
    onNavigate: setActiveIndex,
  })
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    onMatch: isOpen ? setActiveIndex : undefined,
    activeIndex,
  })

  // Inline styles, deliberately: the platform ships a broad global transition
  // rule that outranks Tailwind's `transition-*` utilities, and an inline
  // declaration is the one thing a stylesheet cannot outrank. It also keeps the
  // panel mounted through its exit, which a plain `isOpen &&` cannot.
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    // Reduced motion drops the scale-pop to a plain mount/unmount.
    duration: reduce ? 0 : { open: DURATION_BASE * 1000, close: DURATION_QUICK * 1000 },
    common: { transitionTimingFunction: EASE_OUT_CSS },
    initial: { opacity: 0, transform: "scale(0.97)" },
    open: { opacity: 1, transform: "scale(1)" },
    close: { opacity: 0, transform: "scale(0.98)" },
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    hover,
    click,
    role,
    dismiss,
    listNavigation,
    typeahead,
  ])

  // Selecting anything anywhere in the tree closes the whole tree, so a choice
  // three levels deep does not leave its ancestors hanging open.
  useEffect(() => {
    if (!tree) return
    const closeAll = () => setOpen(false)
    tree.events.on("click", closeAll)
    return () => tree.events.off("click", closeAll)
  }, [tree, setOpen])

  const triggerRef = useMergeRefs([refs.setReference, item.ref])
  const triggerElement = !isNested && isValidElement(trigger) ? (trigger as ReactElement) : null
  /**
   * React 19 passes ref through as an ordinary prop, so the caller's ref lives
   * on props rather than on the element. Reading it here keeps it alive
   * through the cloneElement below, which would otherwise replace it.
   */
  const triggerChildRef = triggerElement
    ? (triggerElement.props as { ref?: typeof refs.setReference }).ref
    : undefined
  const mergedTriggerRef = useMergeRefs([refs.setReference, triggerChildRef])

  const contextValue = useMemo<MenuContextValue>(
    () => ({ getItemProps, activeIndex, setActiveIndex, setOpen, isOpen }),
    [getItemProps, activeIndex, setOpen, isOpen],
  )

  const triggerNode = isNested ? (
    <button
      type="button"
      ref={triggerRef}
      role="menuitem"
      disabled={disabled}
      tabIndex={parent && parent.activeIndex === item.index ? 0 : -1}
      data-open={isOpen ? "" : undefined}
      className={cn(
        V2_MENU_ROW_CLASS,
        "text-v2-popover-foreground",
        V2_MENU_ITEM_HOVER_CLASS,
        "data-[open]:bg-v2-accent",
        "disabled:pointer-events-none disabled:opacity-50",
      )}
      {...getReferenceProps(parent?.getItemProps())}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <ChevronRight size={14} aria-hidden className="shrink-0 opacity-60" />
    </button>
  ) : (
    // `inline-flex`, not `contents`: an element with `display: contents`
    // generates no box, so floating-ui measures a zero-size rect at the origin
    // and the menu lands in the top-left corner instead of under its trigger.
    triggerElement ? (
    cloneElement(triggerElement, {
      ...getReferenceProps(triggerElement.props as Record<string, unknown>),
      ref: mergedTriggerRef,
    } as Partial<typeof triggerElement.props>)
  ) : (
    <span ref={refs.setReference} className="inline-flex" {...getReferenceProps()}>
      {trigger}
    </span>
    )
  )

  const content = (
    <MenuContext.Provider value={contextValue}>
      <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
        {isMounted ? (
          <FloatingPortal>
            <FloatingFocusManager
              context={context}
              modal={false}
              initialFocus={isNested ? -1 : 0}
              returnFocus={!isNested}
            >
              <div
                ref={refs.setFloating}
                style={{ ...overlayStyles, ...transitionStyles }}
                aria-label={ariaLabel}
                className={cn(
                  // `outline-none` matters: FloatingFocusManager focuses this
                  // container, and without it the browser paints a focus ring
                  // around the whole panel.
                  V2_MENU_SURFACE_CLASS,
                  "min-w-[10rem]",
                  contentClassName,
                )}
                {...getFloatingProps()}
              >
                {children}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        ) : null}
      </FloatingList>
    </MenuContext.Provider>
  )

  return (
    <FloatingNode id={nodeId}>
      {triggerNode}
      {content}
    </FloatingNode>
  )
}

/**
 * A menu. Renders its own `FloatingTree` at the root so nested menus share one
 * tree; nested instances detect the existing tree and join it.
 */
export function Menu(props: MenuProps) {
  const parentId = useFloatingParentNodeId()
  if (parentId === null) {
    return (
      <FloatingTree>
        <MenuRoot {...props} />
      </FloatingTree>
    )
  }
  return <MenuRoot {...props} />
}

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Text used for typeahead. Falls back to the children when they are a string. */
  label?: string
  destructive?: boolean
}

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(function MenuItem(
  { children, label, destructive, disabled, className, onClick, ...rest },
  forwardedRef,
) {
  const { getItemProps, activeIndex, setOpen } = useMenuContext()
  const tree = useFloatingTree()
  const typeaheadLabel = label ?? (typeof children === "string" ? children : null)
  const item = useListItem({ label: disabled ? null : typeaheadLabel })
  const ref = useMergeRefs([item.ref, forwardedRef])

  return (
    <button
      type="button"
      ref={ref}
      role="menuitem"
      disabled={disabled}
      tabIndex={activeIndex === item.index ? 0 : -1}
      className={cn(
        V2_MENU_ROW_CLASS,
        V2_MENU_ITEM_HOVER_CLASS,
        destructive
          ? "text-v2-destructive-text hover:bg-v2-destructive/10 focus-visible:bg-v2-destructive/10"
          : "text-v2-popover-foreground",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...getItemProps({
        onClick(event: React.MouseEvent<HTMLButtonElement>) {
          onClick?.(event)
          setOpen(false)
          tree?.events.emit("click")
        },
      })}
      {...rest}
    >
      {children}
    </button>
  )
})

export interface MenuCheckboxItemProps extends Omit<MenuItemProps, "onSelect"> {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

/** A menu item that toggles rather than dismisses, so several can be picked. */
export const MenuCheckboxItem = forwardRef<HTMLButtonElement, MenuCheckboxItemProps>(
  function MenuCheckboxItem(
    { checked, onCheckedChange, children, label, disabled, className, ...rest },
    forwardedRef,
  ) {
    const { getItemProps, activeIndex } = useMenuContext()
    const typeaheadLabel = label ?? (typeof children === "string" ? children : null)
    const item = useListItem({ label: disabled ? null : typeaheadLabel })
    const ref = useMergeRefs([item.ref, forwardedRef])

    return (
      <button
        type="button"
        ref={ref}
        role="menuitemcheckbox"
        aria-checked={checked}
        disabled={disabled}
        tabIndex={activeIndex === item.index ? 0 : -1}
        className={cn(
          V2_MENU_ROW_CLASS,
          "text-v2-popover-foreground",
          V2_MENU_ITEM_HOVER_CLASS,
          "disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
        {...getItemProps({
          // Deliberately does not close the menu or emit the tree click event.
          onClick: () => onCheckedChange(!checked),
        })}
        {...rest}
      >
        <span className="grid size-4 shrink-0 place-items-center">
          {checked ? <Check size={14} aria-hidden /> : null}
        </span>
        <span className="min-w-0 flex-1 truncate">{children}</span>
      </button>
    )
  },
)

export function MenuSeparator({ className }: { className?: string }) {
  // `<hr>` already carries the separator role, so the element and the semantics
  // agree instead of being asserted on a generic div.
  return <hr className={cn("-mx-1 my-1 h-px border-0 bg-v2-border", className)} />
}

/**
 * Heading treatment for a labelled group of rows, shared by {@link MenuLabel}
 * and the v2 Select's own group label so a grouped menu and a grouped select
 * read as siblings. Bold rather than merely muted, and ruled off from the
 * rows below it with the same hairline the row list itself uses for
 * separators — a heading has to look load-bearing, not just quieter text.
 */
export const V2_MENU_GROUP_HEADING_CLASS =
  "mb-1 border-b border-v2-border px-2 pb-1.5 pt-2 text-v2-eyebrow font-semibold uppercase text-v2-foreground"

export function MenuLabel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn(V2_MENU_GROUP_HEADING_CLASS, className)}>{children}</div>
}

export function MenuGroup({ children }: { children: ReactNode }) {
  return <div role="group">{children}</div>
}

interface MenuRadioGroupContextValue {
  value: string
  onValueChange?: (value: string) => void
}

const MenuRadioGroupContext = createContext<MenuRadioGroupContextValue | null>(null)

export interface MenuRadioGroupProps {
  value: string
  onValueChange?: (value: string) => void
  children: ReactNode
  className?: string
}

/** Groups {@link MenuRadioItem}s so only one of them reads as checked. */
export function MenuRadioGroup({ value, onValueChange, children, className }: MenuRadioGroupProps) {
  const context = useMemo(() => ({ value, onValueChange }), [value, onValueChange])
  return (
    <MenuRadioGroupContext.Provider value={context}>
      <div role="group" className={className}>
        {children}
      </div>
    </MenuRadioGroupContext.Provider>
  )
}

export interface MenuRadioItemProps extends Omit<MenuItemProps, "onSelect"> {
  value: string
}

/**
 * A menu item that reads as checked when its `value` matches its group's,
 * styled and wired the same way as {@link MenuCheckboxItem} — it toggles the
 * group's selection rather than dismissing the menu.
 */
export const MenuRadioItem = forwardRef<HTMLButtonElement, MenuRadioItemProps>(function MenuRadioItem(
  { value, children, label, disabled, className, ...rest },
  forwardedRef,
) {
  const group = useContext(MenuRadioGroupContext)
  if (!group) throw new Error("MenuRadioItem must be used inside a <MenuRadioGroup>")
  const { getItemProps, activeIndex } = useMenuContext()
  const typeaheadLabel = label ?? (typeof children === "string" ? children : null)
  const item = useListItem({ label: disabled ? null : typeaheadLabel })
  const ref = useMergeRefs([item.ref, forwardedRef])
  const checked = group.value === value

  return (
    <button
      type="button"
      ref={ref}
      role="menuitemradio"
      aria-checked={checked}
      disabled={disabled}
      tabIndex={activeIndex === item.index ? 0 : -1}
      className={cn(
        V2_MENU_ROW_CLASS,
        "text-v2-popover-foreground",
        V2_MENU_ITEM_HOVER_CLASS,
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...getItemProps({
        // Deliberately does not close the menu, matching MenuCheckboxItem.
        onClick: () => group.onValueChange?.(value),
      })}
      {...rest}
    >
      <span className="grid size-4 shrink-0 place-items-center">
        {checked ? <span aria-hidden className="size-1.5 rounded-full bg-current" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  )
})

interface MenuSubContextValue extends MenuContextValue {
  getReferenceProps: (userProps?: Record<string, unknown>) => Record<string, unknown>
  getFloatingProps: (userProps?: Record<string, unknown>) => Record<string, unknown>
  refs: ReturnType<typeof useFloatingOverlay>["refs"]
  overlayStyles: ReturnType<typeof useFloatingOverlay>["overlayStyles"]
  floatingContext: ReturnType<typeof useFloatingOverlay>["context"]
  isMounted: boolean
  transitionStyles: ReturnType<typeof useTransitionStyles>["styles"]
  elementsRef: MutableRefObject<Array<HTMLButtonElement | null>>
  labelsRef: MutableRefObject<Array<string | null>>
}

const MenuSubContext = createContext<MenuSubContextValue | null>(null)

function useMenuSubContext() {
  const context = useContext(MenuSubContext)
  if (!context) throw new Error("MenuSub parts must be used inside a <MenuSub>")
  return context
}

export interface MenuSubProps {
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
}

/**
 * A submenu, opening to the right of its trigger.
 *
 * Unlike `<Menu>`, it never opens a `FloatingTree` of its own: nested inside a
 * real `<Menu>` it reads the ambient tree, so a leaf selection still closes
 * the whole chain; nested inside a foreign surface such as a v1 Radix
 * `DropdownMenu` — which is exactly what a filter builder does until that
 * surface gets its own v2 rewrite — `useFloatingTree` quietly returns nothing
 * and it behaves as a self-contained popover instead.
 */
export function MenuSub({ children, open: openProp, onOpenChange, disabled }: MenuSubProps) {
  const reduce = useReducedMotion()
  const [internalOpen, setInternalOpen] = useState(false)
  const controlled = openProp !== undefined
  const isOpen = controlled ? openProp : internalOpen
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const elementsRef = useRef<Array<HTMLButtonElement | null>>([])
  const labelsRef = useRef<Array<string | null>>([])

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [controlled, onOpenChange],
  )

  const { refs, overlayStyles, context } = useFloatingOverlay({
    open: isOpen,
    onOpenChange: setOpen,
    placement: "right-start",
    gap: { mainAxis: 4, alignmentAxis: -4 },
  })

  const hover = useHover(context, {
    enabled: !disabled,
    delay: { open: 75 },
    handleClose: safePolygon({ blockPointerEvents: true }),
  })
  const click = useClick(context, { enabled: !disabled })
  const role = useRole(context, { role: "menu" })
  const dismiss = useDismiss(context)
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    nested: true,
    onNavigate: setActiveIndex,
  })
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    onMatch: isOpen ? setActiveIndex : undefined,
    activeIndex,
  })

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: reduce ? 0 : { open: DURATION_BASE * 1000, close: DURATION_QUICK * 1000 },
    common: { transitionTimingFunction: EASE_OUT_CSS },
    initial: { opacity: 0, transform: "scale(0.97)" },
    open: { opacity: 1, transform: "scale(1)" },
    close: { opacity: 0, transform: "scale(0.98)" },
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    hover,
    click,
    role,
    dismiss,
    listNavigation,
    typeahead,
  ])

  const menuContextValue = useMemo<MenuContextValue>(
    () => ({ getItemProps, activeIndex, setActiveIndex, setOpen, isOpen }),
    [getItemProps, activeIndex, setOpen, isOpen],
  )

  const subContextValue = useMemo<MenuSubContextValue>(
    () => ({
      ...menuContextValue,
      getReferenceProps,
      getFloatingProps,
      refs,
      overlayStyles,
      floatingContext: context,
      isMounted,
      transitionStyles,
      elementsRef,
      labelsRef,
    }),
    [
      menuContextValue,
      getReferenceProps,
      getFloatingProps,
      refs,
      overlayStyles,
      context,
      isMounted,
      transitionStyles,
    ],
  )

  // No `MenuContext.Provider` and no `<FloatingList>` here: `MenuSubTrigger`
  // deliberately sits in whatever AMBIENT menu context and list surround this
  // `MenuSub`, so it registers as an item of a real parent `<Menu>` and arrow
  // keys travel across it like any other row. `MenuSubContent` provides its
  // own `MenuContext` and list — built from `elementsRef`/`labelsRef` above —
  // scoped to just the items it renders.
  return <MenuSubContext.Provider value={subContextValue}>{children}</MenuSubContext.Provider>
}

export type MenuSubTriggerProps = ButtonHTMLAttributes<HTMLButtonElement>

/**
 * The row that opens a {@link MenuSub}, styled the same as a `<Menu>` nested
 * inside another `<Menu>`. When it sits inside a real `<Menu>`'s item list it
 * also registers there, so arrow keys travel through it like any other row;
 * inside a foreign surface it simply has no such list to join.
 */
export const MenuSubTrigger = forwardRef<HTMLButtonElement, MenuSubTriggerProps>(
  function MenuSubTrigger({ children, className, disabled, ...rest }, forwardedRef) {
    const sub = useMenuSubContext()
    const parentMenu = useContext(MenuContext)
    const item = useListItem()
    const ref = useMergeRefs([sub.refs.setReference, item.ref, forwardedRef])
    const baseReferenceProps = parentMenu ? parentMenu.getItemProps(rest) : rest
    /**
     * `nested: true` on this submenu's own `useListNavigation` wires ArrowRight
     * to open it, but only wires ArrowLeft to close it on the FLOATING side —
     * which never sees the keystroke, because focus never leaves this trigger
     * (`initialFocus={-1}` in `MenuSubContent`, deliberately, so a hover-opened
     * submenu doesn't steal focus from the pointer). Closing on ArrowLeft while
     * focus sits here, then, has to be wired by hand.
     */
    const referenceProps = {
      ...baseReferenceProps,
      onKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
        ;(baseReferenceProps as { onKeyDown?: (event: ReactKeyboardEvent<HTMLButtonElement>) => void }).onKeyDown?.(
          event,
        )
        if (event.key === "ArrowLeft" && sub.isOpen) sub.setOpen(false)
      },
    }

    return (
      <button
        type="button"
        ref={ref}
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={sub.isOpen}
        disabled={disabled}
        data-state={sub.isOpen ? "open" : "closed"}
        tabIndex={parentMenu ? (parentMenu.activeIndex === item.index ? 0 : -1) : -1}
        className={cn(
          V2_MENU_ROW_CLASS,
          "text-v2-popover-foreground",
          V2_MENU_ITEM_HOVER_CLASS,
          "data-[state=open]:bg-v2-accent",
          "disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
        {...sub.getReferenceProps(referenceProps)}
      >
        <span className="min-w-0 flex-1 truncate">{children}</span>
        <ChevronRight size={14} aria-hidden className="shrink-0 opacity-60" />
      </button>
    )
  },
)

export interface MenuSubContentProps {
  children: ReactNode
  className?: string
}

/**
 * The panel a {@link MenuSub} opens, positioned to the right of its trigger.
 * Its own `MenuContext` and `FloatingList` are scoped here, not on `MenuSub`
 * itself, so a {@link MenuItem}, {@link MenuCheckboxItem} or {@link MenuRadioItem}
 * inside reads this submenu's list rather than an ambient parent's.
 */
export function MenuSubContent({ children, className }: MenuSubContentProps) {
  const sub = useMenuSubContext()
  if (!sub.isMounted) return null

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={sub.floatingContext}
        modal={false}
        initialFocus={-1}
        returnFocus={false}
      >
        <div
          ref={sub.refs.setFloating}
          style={{ ...sub.overlayStyles, ...sub.transitionStyles }}
          className={cn(V2_MENU_SURFACE_CLASS, "min-w-[10rem]", className)}
          {...sub.getFloatingProps()}
        >
          <MenuContext.Provider value={sub}>
            <FloatingList elementsRef={sub.elementsRef} labelsRef={sub.labelsRef}>
              {children}
            </FloatingList>
          </MenuContext.Provider>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}
